import type { Household, User } from '@hewey/shared'
import {
	createContext,
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react'
import { api } from '@api'
import { bindCacheToUser } from '@cache/resources.ts'
import { readSnapshot, writeSnapshot } from './snapshot.ts'

type AuthState =
	| { status: 'loading' }
	| { status: 'anonymous' }
	| { status: 'authed'; user: User; household: Household }

type AuthCtx = {
	state: AuthState
	setSession: (user: User, household: Household) => void
	// Merge one half of the session in place. Settings renames the user and
	// the household independently, and `setSession` demands both.
	patchSession: (next: { user?: User; household?: Household }) => void
	// Drop to anonymous without calling the server. Deleting your account has
	// already taken the session row and cleared the cookie, so a logout
	// request afterwards would be a round trip to revoke nothing.
	endSession: () => void
	logout: () => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	// Start signed in on the stored snapshot so the first paint is content,
	// not the splash. /auth/me corrects it below.
	const [state, setState] = useState<AuthState>(() => {
		const snapshot = readSnapshot()
		return snapshot
			? {
					status: 'authed',
					user: snapshot.user,
					household: snapshot.household,
				}
			: { status: 'loading' }
	})

	// Every change of identity goes through here, and empties the cache
	// before the state update rather than in an effect afterwards: effects run
	// children-first, so a clear from up here would land after the newly
	// mounted mini-app had already started fetching, and throw its request
	// away.
	const setIdentity = useCallback((next: AuthState) => {
		bindCacheToUser(next.status === 'authed' ? next.user.id : null)
		setState(next)
	}, [])

	useEffect(() => {
		let cancelled = false
		api.authMe()
			.then((res) => {
				if (cancelled) return
				setIdentity(
					res.user && res.household
						? {
								status: 'authed',
								user: res.user,
								household: res.household,
							}
						: { status: 'anonymous' },
				)
			})
			.catch(() => {
				// A failed check is not a sign-out. On a snapshot we stay on
				// the cached view; with nothing to show, fall to the sign-in
				// screen.
				if (!cancelled) {
					setState((prev) =>
						prev.status === 'loading'
							? { status: 'anonymous' }
							: prev,
					)
				}
			})
		return () => {
			cancelled = true
		}
	}, [setIdentity])

	// One place to mirror the session outwards, covering sign-in, onboarding,
	// a rename, a sign-out and whatever /auth/me came back with.
	useEffect(() => {
		if (state.status === 'loading') return
		writeSnapshot(
			state.status === 'authed'
				? { user: state.user, household: state.household }
				: null,
		)
	}, [state])

	const setSession = useCallback(
		(user: User, household: Household) => {
			setIdentity({ status: 'authed', user, household })
		},
		[setIdentity],
	)

	const patchSession = useCallback(
		(next: { user?: User; household?: Household }) => {
			setState((prev) =>
				prev.status === 'authed'
					? {
							...prev,
							user: next.user ?? prev.user,
							household: next.household ?? prev.household,
						}
					: prev,
			)
		},
		[],
	)

	const endSession = useCallback(() => {
		setIdentity({ status: 'anonymous' })
	}, [setIdentity])

	const logout = useCallback(async () => {
		await api.authLogout().catch(() => undefined)
		endSession()
	}, [endSession])

	const value = useMemo(
		() => ({ state, setSession, patchSession, endSession, logout }),
		[state, setSession, patchSession, endSession, logout],
	)

	// Rebuild the whole signed-in surface when the account changes. Clearing
	// the cache doesn't reach the data a mounted mini-app is already holding
	// in its own state, and a stale snapshot corrected by /auth/me would
	// otherwise leave the previous account's content on screen.
	return (
		<Ctx.Provider value={value}>
			<Fragment key={state.status === 'authed' ? state.user.id : 'anon'}>
				{children}
			</Fragment>
		</Ctx.Provider>
	)
}

export const useAuth = (): AuthCtx => {
	const ctx = useContext(Ctx)
	if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
	return ctx
}
